import React, { useState, useRef, useEffect } from "react";
import {
  DropdownContainer,
  DropdownHeader,
  DropdownList,
  DropdownItem,
  SearchInput,
  SelectedItem,
  SelectedItemsList,
  ChevronIcon,
  DropdownPlaceholder,
  NoResults,
  Label,
  DropdownItemsContainer,
} from "./Dropdown.styled";
import { Portal } from "@mui/material";
import { Checkbox, Radio } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckIcon from "@mui/icons-material/Check";

const Dropdown = ({
  options,
  placeholder = "Select...",
  multiple = false,
  searchable = true,
  value,
  onChange,
  maxSelected,
  disabled = false,
  label,
  required,
  error,
}) => {
  const selectedValue = multiple ? (Array.isArray(value) ? value : []) : value;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const [listPosition, setListPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const headerRef = useRef(null);
  const [dropDirection, setDropDirection] = useState("down");
  const [dropdownWidth, setDropdownWidth] = useState(0);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredValues = filteredOptions.map((o) => o.value);
  const hasMaxSelected =
    multiple &&
    typeof maxSelected === "number" &&
    Number.isFinite(maxSelected) &&
    maxSelected > 0;
  const reachedMax =
    hasMaxSelected &&
    selectedValue.length >= maxSelected;
  const allOptionsSelected =
    multiple &&
    Array.isArray(options) &&
    options.length > 0 &&
    options.every((o) => selectedValue.includes(o.value));
  const allFilteredSelected =
    multiple &&
    filteredOptions.length > 0 &&
    filteredValues.every((v) => selectedValue.includes(v));
  const someFilteredSelected =
    multiple &&
    filteredOptions.length > 0 &&
    filteredValues.some((v) => selectedValue.includes(v)) &&
    !allFilteredSelected;

  const handleSelectAllFiltered = (e) => {
    e.stopPropagation();
    if (!multiple || filteredOptions.length === 0) return;
    if (allFilteredSelected) {
      onChange(selectedValue.filter((v) => !filteredValues.includes(v)));
    } else {
      onChange([...new Set([...selectedValue, ...filteredValues])]);
    }
  };

  const handleSelect = (option) => (e) => {
    e.stopPropagation();

    if (multiple) {
      const isSelected = selectedValue.includes(option.value);
      if (!isSelected && reachedMax) {
        return;
      }
      const newValue = selectedValue.includes(option.value)
        ? selectedValue.filter((v) => v !== option.value)
        : [...selectedValue, option.value];
      onChange(newValue);
    } else {
      const newValue = selectedValue === option.value ? null : option.value;
      onChange(newValue);
      setIsOpen(false);
    }
  };

  const handleSearchClick = (e) => {
    e.stopPropagation();
  };

  const handleDelete = (optionValue) => (event) => {
    event.stopPropagation();
    const newValue = selectedValue.filter((v) => v !== optionValue);
    onChange(newValue);
  };

  const getDisplayValue = () => {
    if (multiple) {
      if (allOptionsSelected) {
        return (
          <SelectedItemsList>
            <SelectedItem
              key="__select_all_chip__"
              label="Select all"
              onDelete={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
              size="small"
            />
          </SelectedItemsList>
        );
      }

      const selectedLabels = selectedValue
        .map((v) => options.find((opt) => opt.value === v))
        .filter(Boolean);

      return selectedLabels.length > 0 ? (
        <SelectedItemsList>
          {selectedLabels.map((option) => (
            <SelectedItem
              key={option.value}
              label={option.label}
              onDelete={handleDelete(option.value)}
              size="small"
            />
          ))}
        </SelectedItemsList>
      ) : (
        <DropdownPlaceholder>{placeholder}</DropdownPlaceholder>
      );
    }

    if (selectedValue) {
      const option = options.find((opt) => opt.value === selectedValue);
      return option ? (
        <div className="truncated-text">{option.label}</div>
      ) : (
        <DropdownPlaceholder>{placeholder}</DropdownPlaceholder>
      );
    }

    return <DropdownPlaceholder>{placeholder}</DropdownPlaceholder>;
  };

  useEffect(() => {
    const updateWidth = () => {
      if (headerRef.current) {
        setDropdownWidth(headerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".dropdown-list")) {
        return;
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    const handleScroll = (event) => {
      if (event.target instanceof Element && event.target.closest(".dropdown-list")) {
        return;
      }

      if (isOpen) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && headerRef.current) {
      const rect = headerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 200; // Maximum dropdown height

      const shouldDropUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      setDropDirection(shouldDropUp ? 'up' : 'down');

      // Calculate position relative to the viewport
      const position = {
        left: rect.left,
        width: rect.width,
      };

      if (shouldDropUp) {
        position.bottom = window.innerHeight - rect.top;
        position.top = 'auto';
      } else {
        position.top = rect.bottom;
        position.bottom = 'auto';
      }

      setListPosition(position);
    }
  }, [isOpen]);

  const renderOption = (option) => {
    const isItemSelected = multiple
      ? selectedValue.includes(option.value)
      : selectedValue === option.value;
    const isItemDisabled = multiple ? (!isItemSelected && reachedMax) : false;

    return (
      <DropdownItem
        key={option.value}
        onClick={handleSelect(option)}
        isSelected={isItemSelected}
        multiple={multiple}
        style={isItemDisabled ? { opacity: 0.55, pointerEvents: "none" } : undefined}
      >
        <div className="selection-indicator">
          {multiple ? (
            <Checkbox
              checked={isItemSelected}
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              tabIndex={-1}
              disableRipple
              size="small"
              style={{ pointerEvents: "none" }}
            />
          ) : (
            isItemSelected && <CheckIcon />
          )}
        </div>
        <span className="item-label">{option.label}</span>
      </DropdownItem>
    );
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      {label && (
        <Label>
          {label}
          {required && <span className="required"> *</span>}
        </Label>
      )}
       <DropdownHeader
        ref={headerRef}
        isOpen={isOpen}
        disabled={disabled}
        error={error}
        dropdownWidth={dropdownWidth}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            !disabled && setIsOpen(!isOpen);
          }
        }}
      >
        {getDisplayValue()}
        <ChevronIcon isOpen={isOpen} />
      </DropdownHeader>
      {isOpen && (
        <Portal>
          <DropdownList
            className="dropdown-list"
            style={{
              top: listPosition.top,
              bottom: listPosition.bottom,
              left: listPosition.left,
              width: listPosition.width,
            }}
            data-popper-placement={dropDirection === "up" ? "top" : "bottom"}
          >
            {searchable && (
              <SearchInput
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={handleSearchClick}
                onKeyDown={(e) => e.stopPropagation()}
                autoFocus
              />
            )}
            <DropdownItemsContainer>
              {multiple && !hasMaxSelected && filteredOptions.length > 0 && (
                <DropdownItem
                  key="__select_all__"
                  onClick={handleSelectAllFiltered}
                  isSelected={allFilteredSelected}
                  multiple={multiple}
                >
                  <div className="selection-indicator">
                    <Checkbox
                      checked={allFilteredSelected}
                      indeterminate={someFilteredSelected}
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                      style={{ pointerEvents: "none" }}
                    />
                  </div>
                  <span className="item-label">Select all</span>
                </DropdownItem>
              )}
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => renderOption(option))
              ) : (
                <NoResults>No results found</NoResults>
              )}
            </DropdownItemsContainer>
          </DropdownList>
        </Portal>
      )}
    </DropdownContainer>
  );
};

export default Dropdown;
